import { useEffect, useState } from 'react'
import { CATEGORY_SETUP, useEditor } from '../store/useEditor'
import {
  deleteProject,
  duplicateProject,
  loadProject,
  setProjectStatus,
  updateJobsCustomer,
  type ProjectSummary,
} from '../lib/db'
import type { LightsOsDocument } from '../types/document'
import { gateWording, sectionTotals, sectionsOf } from '../lib/sections'
import { currentApproval, driftFrom } from '../lib/approval'
import { takeoff } from '../lib/materials'
import { PRODUCTS } from '../lib/products'
import { JOB_STATUS, type JobStatus } from '../types/document'
import { docState, moves } from '../lib/estimates'

import { Empty, SaidLine, useSaid } from './bits'


import {
  balanceDue,
  depositAmount,
  discount,
  discountLabel,
  docNumber,
  dueWording,
  longDate,
  money,
  normalizePaper,
  offFraction,
  paidAmount,
  priceCents,
  subtotal,
  total,
} from '../lib/paper'

/**
 * ONE HOUSE, EVERYTHING ABOUT IT.
 *
 * The job list answers "which house was that" and the editor answers "what do
 * the lights look like". Neither answers the question somebody actually opens
 * a job to ask in December: where does this one stand. What did we quote, has
 * any of it been paid, what is left, and what do I do next.
 *
 * So the row opens here rather than dropping straight into the editor. The
 * design is one press away and always has been; what was missing was the page
 * in between, and without it the quote, the invoice and the payments were only
 * reachable by loading the photograph first.
 *
 * IT READS THE DOCUMENT, IT DOES NOT RECOMPUTE IT. Every figure comes from the
 * same paper functions the printed sheet uses, so this page and the page the
 * homeowner receives cannot disagree.
 */

/** Measured at the house, and confirmed. Anything else is a guess. */
const isMeasured = (sec: { measurement: { source: string; verifiedAt: string | null } }) =>
  sec.measurement.source === 'field' && !!sec.measurement.verifiedAt

const statusLabel = (s: JobStatus) => JOB_STATUS.find((x) => x.id === s)?.label ?? 'Working on it'
const pillTone = (s: JobStatus) =>
  s === 'won' ? 'won' : s === 'lost' ? 'lost' : s === 'quoted' ? 'quoted' : ''

export default function JobOverview({
  job,
  /**
   * Rendered as the first tab of the job record rather than as its own
   * screen, so the head belongs to the record and this draws only the body.
   * Two names, two addresses and two status pills on one page was the whole
   * reason the merge needed a flag rather than a copy.
   */
  chromeless,
  onBack,
  onChanged,
  onOpenDesign,
  onOpenQuote,
}: {
  job: ProjectSummary
  chromeless?: boolean
  onBack: () => void
  onChanged: () => void
  onOpenDesign: (doc: LightsOsDocument) => void
  onOpenQuote: (doc: LightsOsDocument) => void
}) {

  const loadDocument = useEditor((s) => s.loadDocument)
  const prefix = useEditor((s) => s.company.prefix)
  const [doc, setDoc] = useState<LightsOsDocument | null>(null)
  const [missing, setMissing] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const { said, say } = useSaid()

  /*
   * NAMING A JOB WITHOUT OPENING IT.
   *
   * A job gets its customer from the quote screen, which is behind loading the
   * photograph, which is behind having a reason to open the design. So a
   * season of walkthroughs ends up as a list of jobs called Untitled mockup,
   * and in December there is no way to find the Ridgeline house except by
   * recognizing its roof. Thirty-seven of them, reported from a phone.
   *
   * The details belong to the job, so they are edited on the job's own page.
   * It opens by itself when there is no name yet, because that is the one
   * state where somebody should be nudged, and folds away when there is.
   */
  const [editing, setEditing] = useState(false)
  const [customer, setCustomer] = useState(job.customer)
  const [address, setAddress] = useState(job.address)
  const [phone, setPhone] = useState(job.phone)
  const [saving, setSaving] = useState(false)

  const named = customer.trim().length > 0
  const openEditor = editing || !named
  const dirty =
    customer !== job.customer || address !== job.address || phone !== job.phone

  const saveDetails = async () => {
    setSaving(true)
    try {
      await updateJobsCustomer([job.id], { customer, address, phone })
      onChanged()
      setEditing(false)
      say('Saved.')
    } catch {
      say('That could not be saved on this device.', 'bad')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    let alive = true
    loadProject(job.id)
      .then((d) => {
        if (!alive) return
        if (d) setDoc(d)
        else setMissing(true)
      })
      .catch(() => alive && setMissing(true))
    return () => {
      alive = false
    }
  }, [job.id])

  const lines = doc?.quote ?? []
  const paper = normalizePaper(doc?.paper, lines)
  const tot = total(lines, paper)
  const received = paidAmount(lines, paper)
  const owed = balanceDue(lines, paper)
  const dep = depositAmount(lines, paper)
  const off = offFraction(paper)
  const sub = subtotal(lines)
  const disc = discount(lines, paper)
  const number = docNumber(paper, prefix)

  const invoice = paper.kind === 'invoice'
  const priced = tot > 0

  /*
   * WHAT THE HOUSE ITSELF SAYS.
   *
   * All of this existed and none of it was on this page: a job could carry two
   * walked sections, a scope frozen in September and a front eave that had
   * moved since, and the only way to learn any of it was to load the
   * photograph and open the Measure sheet. This is the page somebody opens in
   * December to ask where a job stands, so it has to be able to answer.
   *
   * Read, never recomputed differently. The same functions the Measure sheet
   * uses, so the two screens cannot disagree about the same house.
   */
  const sections = doc ? sectionsOf(doc) : []
  const secTotals = sectionTotals(sections)
  const gate = gateWording(secTotals)
  const approved = doc ? currentApproval(doc) : null
  const drift = approved ? driftFrom(approved, sections) : []
  const takeoffLines = takeoff(sections, PRODUCTS.C9.spacingInches).lines
  const bulbs = takeoffLines.find((l) => l.key === 'bulbs')?.qty ?? 0

  const open = (then: (d: LightsOsDocument) => void) => {
    if (!doc) return
    loadDocument(doc)
    then(doc)
  }

  return (
    <>
      {/*
        THE BREADCRUMB IS A PLACE, NOT A STATUS.

        It read "Jobs / Not quoted yet" while a green Approved pill sat below
        it, which is two different statuses contradicting each other on one
        screen. Back is the app bar above; the document number is a fact about
        the job and sits with the money.
      */}
      {!chromeless && (
        <div className="ws-head">
          <div>
            <h1>{customer || job.name}</h1>
            <p>
              {[address.split('\n')[0], number, `Saved ${longDate(job.savedAt.slice(0, 10))}`]
                .filter(Boolean)
                .join('  ·  ')}
            </p>
          </div>
          <div className="ws-head-acts">
            <span className={`ws-pill ${pillTone(job.status)}`}>{statusLabel(job.status)}</span>
            <button
              className="btn primary"
              onClick={() => open(onOpenDesign)}
              disabled={!doc || !doc.photo}
              title={doc && !doc.photo ? 'This job lost its photograph' : 'Open the design'}
            >
              Open the design
            </button>
          </div>
        </div>
      )}

      {missing && (

        <div className="ws-card">
          <Empty kind="wrong">This job could not be opened on this device.</Empty>
        </div>
      )}

      {/*
        TWO COLUMNS, BECAUSE THIS IS A RECORD.

        Every card on this page used to run the full width of the screen, so a
        single line saying who owns the house occupied a two hundred pixel
        band, and the four figures sat in tiles tall enough to read from
        across the room. A record screen is read the way the design package
        draws it: the work down the middle, the facts and the ways to act on
        them in a rail beside it, and everything at the size of something you
        read rather than something you present.
      */}
      <div className="rec-grid">
        <div className="rec-main">
          {doc && (
            <>
              {/*
                WHERE THIS HOUSE STANDS, as opposed to where the paperwork
                stands. All of this existed and none of it was on this page: a
                job could carry two walked sections, a scope frozen in
                September and a front eave that had moved since, and the only
                way to learn any of it was to load the photograph.
              */}
              <div className="ws-card ws-section">
                <div className="job-measure-head">
                  <h2 className="job-h2">The house</h2>
                  <button
                    className="btn ghost"
                    onClick={() => open(onOpenDesign)}
                    disabled={!doc.photo}
                  >
                    Open Measure
                  </button>
                </div>

                {sections.length === 0 ? (
                  <p className="hint" style={{ margin: 0 }}>
                    No sections yet. Open the design and add one for each part of the roof, then a
                    length that says whether anybody walked it.
                  </p>
                ) : (
                  <>
                    <p className={gate ? 'sec-gate warn' : 'sec-gate ok'} style={{ marginTop: 0 }}>
                      {gate ?? 'Every included section has been measured at the house.'}
                    </p>

                    <ul className="job-secs">
                      {sections.map((sec) => (
                        <li key={sec.id} className={sec.excluded ? 'off' : ''}>
                          <span className="n">{sec.label.trim() || 'Unnamed section'}</span>
                          <span className="f">
                            {sec.measurement.feet > 0 ? `${sec.measurement.feet} ft` : 'No length'}
                          </span>
                          <span className={isMeasured(sec) ? 'st ok' : 'st warn'}>
                            {sec.excluded ? 'Left out' : isMeasured(sec) ? 'Measured' : 'Not measured'}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {secTotals.feet > 0 && (
                      <p className="hint" style={{ margin: '10px 0 0' }}>
                        That is {bulbs} C9 bulbs and clips at twelve inch spacing.
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* THE YES, AND WHAT HAS MOVED SINCE IT. */}
              {approved && (
                <div className="ws-card ws-section">
                  <h2 className="job-h2">The agreed scope</h2>
                  <p style={{ margin: '0 0 4px' }}>
                    <strong>{approved.feet} ft</strong> across {approved.sections.length}{' '}
                    {approved.sections.length === 1 ? 'section' : 'sections'}
                    {approved.packageName ? `, ${approved.packageName}` : ''}, approved by{' '}
                    {approved.by} on {longDate(approved.at.slice(0, 10))}.
                  </p>
                  {approved.note && <p className="hint" style={{ margin: 0 }}>{approved.note}</p>}

                  {drift.length > 0 ? (
                    <>
                      <p className="sec-gate warn" style={{ marginBottom: 2 }}>
                        {drift.length === 1
                          ? 'One thing has changed since this was approved.'
                          : `${drift.length} things have changed since this was approved.`}
                      </p>
                      <ul className="job-drift">
                        {drift.map((d) => (
                          <li key={`${d.kind}-${d.sectionId}`}>{d.said}</li>
                        ))}
                      </ul>
                      <p className="hint" style={{ margin: '6px 0 0' }}>
                        The agreed scope above has not changed. Approving again is what agrees to
                        the new one.
                      </p>
                    </>
                  ) : (
                    <p className="sec-gate ok" style={{ margin: '4px 0 0' }}>
                      The job still matches what was agreed.
                    </p>
                  )}
                </div>
              )}

              {/*
                THE LINES, WHICH THIS PAGE HAS NEVER SHOWN.

                It printed a total and the word Quote and a document number,
                and to find out what was actually on it you loaded the
                photograph and opened the quote sheet. What a person wants to
                know in December is what was included, and that is four rows.
                Read off the same paper functions as the printed sheet, so
                this page and the page the homeowner has cannot disagree.
              */}
              <div className="ws-card ws-section">
                <div className="job-measure-head">
                  <h2 className="job-h2">{invoice ? 'The invoice' : 'The quote'}</h2>
                  <button className="btn ghost" onClick={() => open(onOpenQuote)}>
                    {priced ? 'Open it' : 'Price this job'}
                  </button>
                </div>

                {priced ? (
                  <>
                    <ul className="job-lines">
                      {lines.map((l) => (
                        <li key={l.id}>
                          <span className="jl-what">
                            <strong>{l.label?.trim() || CATEGORY_SETUP[l.category].label}</strong>
                            {l.lighting.trim() && <em>{l.lighting}</em>}
                          </span>
                          <span className="jl-amt">{money(priceCents(l, off) / 100)}</span>
                        </li>
                      ))}
                    </ul>
                    <dl className="job-sums">
                      {off > 0 && (
                        <>
                          <div>
                            <dt>Subtotal</dt>
                            <dd>{money(sub)}</dd>
                          </div>
                          <div>
                            <dt>{discountLabel(paper)}</dt>
                            <dd>-{money(disc)}</dd>
                          </div>
                        </>
                      )}
                      <div className="is-total">
                        <dt>{invoice && owed > 0 ? 'Balance due' : 'Total'}</dt>
                        <dd>{money(invoice && owed > 0 ? owed : tot)}</dd>
                      </div>
                    </dl>
                    {/*
                      Never a payment nobody made. The list is what is on the
                      paper, and an empty list says nothing rather than zero.
                    */}
                    {paper.payments.length > 0 && (
                      <ul className="job-pays">
                        <li className="jp-head">
                          {paper.payments.length === 1 ? 'Received' : 'Received so far'}
                        </li>

                        {paper.payments.map((q) => (
                          <li key={q.id}>
                            {money(q.amount)} on {longDate(q.date)}
                            {q.method ? ` · ${q.method}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                    {dep > 0 && received < dep && (
                      <p className="hint" style={{ margin: '8px 0 0' }}>
                        {money(dep)} deposit requested.
                      </p>
                    )}
                    {invoice && owed > 0 && (
                      <p className="hint" style={{ margin: '4px 0 0' }}>{dueWording(paper)}</p>
                    )}
                  </>
                ) : (
                  <p className="hint" style={{ margin: 0 }}>
                    Nothing priced yet. Open the quote to put lines and a number on it.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <aside className="rec-rail">
          {/*
            THE PICTURE FIRST, because the thing a person recognizes a job by
            is the house. It used to be a gray square beside a sentence.
          */}
          {job.thumb && (
            <div className="job-shot">
              <img src={job.thumb} alt="" />
            </div>
          )}

          {doc && (
            <div className="ws-card rec-figs">
              <dl>
                <div>
                  <dt>{invoice ? 'Invoiced' : 'Quoted'}</dt>
                  <dd>{priced ? money(tot) : 'Not priced'}</dd>
                </div>
                {priced && (
                  <div>
                    <dt>{owed > 0 ? 'Still owed' : 'Settled'}</dt>
                    <dd>{money(owed)}</dd>
                  </div>
                )}
                {secTotals.included > 0 && (
                  <div>
                    <dt>On the house</dt>
                    <dd>
                      {secTotals.feet} ft
                      {secTotals.unverifiedFeet > 0 && (
                        <em>{Math.round(secTotals.unverifiedFeet)} ft not measured</em>
                      )}
                    </dd>
                  </div>
                )}
                <div>
                  <dt>{doc.layers.length === 1 ? 'Run drawn' : 'Runs drawn'}</dt>
                  <dd>
                    {doc.layers.length}
                    {!doc.photo && <em>The photograph is missing</em>}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          <div className="ws-card rec-who">
            <div className="rec-who-head">
              <p className="insp-lbl plain">Whose house this is</p>
              {named && !editing && (
                <button className="link-btn" onClick={() => setEditing(true)}>
                  Edit
                </button>
              )}
            </div>

            {openEditor ? (
              <div className="ws-form rec-who-form">
                {!named && (
                  <p className="hint ws-wide">
                    A job with no name on it is a job you cannot find in December. Ten seconds now.
                  </p>
                )}
                <label className="ws-wide">
                  <span>Customer</span>
                  <input
                    type="text"
                    value={customer}
                    placeholder="The name on the door"
                    onChange={(e) => setCustomer(e.target.value)}
                  />
                </label>
                <label className="ws-wide">
                  <span>Phone</span>
                  <input
                    type="text"
                    value={phone}
                    placeholder="(555) 555-5555"
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </label>
                <label className="ws-wide">
                  <span>Address</span>
                  <textarea
                    rows={2}
                    value={address}
                    placeholder="Street, city"
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </label>
                <div className="ws-wide ws-actions job-save">
                  <SaidLine said={said} />
                  {named && editing && (
                    <button
                      className="btn ghost"
                      onClick={() => {
                        setCustomer(job.customer)
                        setAddress(job.address)
                        setPhone(job.phone)
                        setEditing(false)
                      }}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    className="btn primary"
                    onClick={() => void saveDetails()}
                    disabled={!dirty || saving}
                  >
                    {saving ? 'Saving' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              /* Rows, not one run-on sentence of everything joined by dots. */
              <dl className="rec-contact">
                <div>
                  <dt>Name</dt>
                  <dd>{customer}</dd>
                </div>
                {address.trim() && (
                  <div>
                    <dt>Address</dt>
                    <dd>{address}</dd>
                  </div>
                )}
                {phone.trim() && (
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>{phone}</a>
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </div>

          {/*
            THE WAYS TO ACT ON IT, STACKED AND RANKED.

            They were four buttons of one weight in a row across the bottom of
            the page, with Delete among them wearing the same outline as Copy
            this house. They are ordered now by how often a person means them,
            and the one that cannot be undone sits under a rule, on its own,
            in the color that says so.
          */}
          {doc && (
            <div className="ws-card rec-acts">
              <button className="btn" onClick={() => open(onOpenQuote)}>
                {priced ? `Open the ${invoice ? 'invoice' : 'quote'}` : 'Price this job'}
              </button>
              {/*
                THE OUTCOMES, NAMED. This was one button cycling all four
                states, so it read "Mark not sent yet" - a state written as a
                command, which reads as a negation - and pressing it on a lost
                job put that job back at the beginning. The estimates list
                already answered this question properly; the job's own page
                asks the same one, so it now gets the same answer.
              */}
              {moves(docState(job)).map((m) => (
                <button
                  key={m.to}
                  className="btn"
                  onClick={async () => {
                    await setProjectStatus(job.id, m.to)
                    onChanged()
                  }}
                >
                  {m.label}
                </button>
              ))}

              <button
                className="btn"
                onClick={async () => {
                  const ok = await duplicateProject(job.id, `job_${Date.now().toString(36)}`)
                  onChanged()
                  if (ok) say('Copied. It is at the top of your jobs.')
                  else say('That job could not be copied.', 'bad')
                }}
              >
                Copy this house
              </button>
              <SaidLine said={said} />
              <div className="rec-danger">
                {confirm ? (
                  <>
                    <p className="hint">
                      This removes the house, the design and everything priced on it.
                    </p>
                    <button
                      className="btn danger"
                      onClick={async () => {
                        await deleteProject(job.id)
                        onChanged()
                        onBack()
                      }}
                    >
                      Delete for good
                    </button>
                    <button className="btn ghost" onClick={() => setConfirm(false)}>
                      Keep it
                    </button>
                  </>
                ) : (
                  <button className="btn ghost" onClick={() => setConfirm(true)}>
                    Delete this job
                  </button>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </>
  )
}
